import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FeedbackService } from '../../services/feedback.service';
import { BranchToRate, ProductToRate, BranchFeedback, ProductFeedback, FeedbackResponse } from '../../interfaces/feedback';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-feedback',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule
  ],
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.scss']
})
export class FeedbackComponent implements OnInit {
  loading = true;
  submitting = false;
  feedbackData: FeedbackResponse | null = null;
  branchFeedbacks: BranchFeedback[] = [];
  productFeedbacks: ProductFeedback[] = [];
  ratingOptions = [1, 2, 3, 4, 5];
  error: string | null = null;
  hoverRating = 0;
  submitted = false;

  // Step control
  currentStep = 0;
  totalSteps = 0;
  isBranchStep = true;
  currentBranchIndex = 0;
  currentProductIndex = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private feedbackService: FeedbackService
  ) { }

  ngOnInit(): void {
    const locator = this.route.snapshot.paramMap.get('locator');
    const reservationItemId = this.route.snapshot.paramMap.get('reservationItemId');
    if (!locator || !reservationItemId) {
      this.error = 'Error mostrando feedback';
      this.loading = false;
      // Redirigir al home después de 3 segundos si no hay locator
      setTimeout(() => {
        this.router.navigate(['/']);
      }, 3000);
      return;
    }

    this.feedbackService.getFeedbackData(locator, +reservationItemId).subscribe({
      next: (data) => {
        this.feedbackData = data;
        this.initializeFeedbacks();
        this.initializeSteps();
        this.loading = false;
      },
      error: (error) => {
        this.error = 'Error al cargar los datos del feedback';
        this.loading = false;
        // Redirigir al home después de 3 segundos si hay error
        setTimeout(() => {
          this.router.navigate(['/']);
        }, 3000);
      }
    });
  }

  private initializeFeedbacks(): void {
    if (!this.feedbackData) return;

    this.branchFeedbacks = this.feedbackData.branchesToRate.map(branch => ({
      branchId: branch.id,
      rating: 0,
      comment: ''
    }));

    if (this.feedbackData.requiresProductFeedback) {
      this.productFeedbacks = this.feedbackData.productsToRate.map(product => ({
        productId: product.id,
        rating: 0,
        comment: ''
      }));
    }
  }

  private initializeSteps(): void {
    if (!this.feedbackData) return;
    
    this.totalSteps = this.feedbackData.branchesToRate.length;
    if (this.feedbackData.requiresProductFeedback) {
      this.totalSteps += this.feedbackData.productsToRate.length;
    }
  }

  getCurrentItem(): { name: string; type: 'branch' | 'product' } | null {
    if (!this.feedbackData) return null;

    if (this.isBranchStep) {
      return {
        name: this.feedbackData.branchesToRate[this.currentBranchIndex].name,
        type: 'branch'
      };
    } else {
      return {
        name: this.feedbackData.productsToRate[this.currentProductIndex].name,
        type: 'product'
      };
    }
  }

  getCurrentFeedback(): BranchFeedback | ProductFeedback | null {
    if (this.isBranchStep) {
      return this.branchFeedbacks[this.currentBranchIndex];
    } else {
      return this.productFeedbacks[this.currentProductIndex];
    }
  }

  canGoNext(): boolean {
    const currentFeedback = this.getCurrentFeedback();
    if (!currentFeedback) return false;
    return this.isValidRating(currentFeedback.rating);
  }

  goNext(): void {
    if (!this.canGoNext()) return;
    this.hoverRating = 0; // Reset hover state when moving to next item

    if (this.isBranchStep) {
      if (this.currentBranchIndex < this.feedbackData!.branchesToRate.length - 1) {
        this.currentBranchIndex++;
      } else if (this.feedbackData?.requiresProductFeedback) {
        this.isBranchStep = false;
        this.currentProductIndex = 0;
      }
    } else {
      if (this.currentProductIndex < this.feedbackData!.productsToRate.length - 1) {
        this.currentProductIndex++;
      }
    }
    this.currentStep++;
  }

  goPrevious(): void {
    this.hoverRating = 0; // Reset hover state when moving to previous item
    
    if (this.isBranchStep) {
      if (this.currentBranchIndex > 0) {
        this.currentBranchIndex--;
      }
    } else {
      if (this.currentProductIndex > 0) {
        this.currentProductIndex--;
      } else {
        this.isBranchStep = true;
        this.currentBranchIndex = this.feedbackData!.branchesToRate.length - 1;
      }
    }
    this.currentStep--;
  }

  isLastStep(): boolean {
    return this.currentStep === this.totalSteps - 1;
  }

  isValidRating(rating: number): boolean {
    return rating >= 1 && rating <= 5;
  }

  canSubmit(): boolean {
    const validBranchRatings = this.branchFeedbacks.every(bf => this.isValidRating(bf.rating));
    
    if (this.feedbackData?.requiresProductFeedback) {
      return validBranchRatings && this.productFeedbacks.every(pf => this.isValidRating(pf.rating));
    }
    
    return validBranchRatings;
  }

  onSubmit(): void {
    if (!this.feedbackData || !this.canSubmit()) return;

    this.submitting = true;
    const feedback = {
      locator: this.feedbackData.info.locator,
      reservationItemId: this.feedbackData.info.itemId,
      branchFeedbacks: this.branchFeedbacks,
      productFeedbacks: this.feedbackData.requiresProductFeedback ? this.productFeedbacks : undefined
    };


    this.feedbackService.submitFeedback(feedback).subscribe({
      next: () => {
        this.submitting = false;
        this.submitted = true;
        // Redirigir al home después de 5 segundos
        setTimeout(() => {
          this.router.navigate(['/']);
        }, 5000);
      },
      error: (error) => {
        this.error = 'Error al enviar el feedback';
        this.submitting = false;
        // Redirigir al home después de 3 segundos si hay error al enviar
        setTimeout(() => {
          this.router.navigate(['/']);
        }, 3000);
      }
    });
 }
}
